import { Controller } from "@hotwired/stimulus"

// Two-way sync between Start time, Duration (minutes), and End time.
// The start/end fields are `time-select` comboboxes (their roots are
// this controller's start/end targets); the duration is a plain
// number input. We read/write the time values through the
// time-select controller instances and listen for their
// `time-select:change` events (wired in the partial).
//
//   - Type a duration → end = start + duration
//   - Pick an end time → duration = end − start
//   - Change the start time → hold the duration, slide the end
//
// All math in minutes-since-midnight; crossing midnight wraps by a day.
export default class extends Controller {
  static targets = ["start", "duration", "end"]

  // Fired by either time-select (start or end) via the bubbling
  // `time-select:change` event. event.currentTarget is the
  // time-select root that the action is bound to.
  timeChanged(event) {
    if (event.currentTarget === this.startTarget) {
      this._startChanged()
    } else if (event.currentTarget === this.endTarget) {
      this._recalcDuration()
    }
  }

  durationChanged() {
    const start = this._toMinutes(this._startCtl()?.value)
    const dur = this._intOrNull(this.durationTarget.value)
    if (start == null || dur == null) return
    this._setEndFromDuration(start, dur)
  }

  // ─── private ──────────────────────────────────────────────────

  _startChanged() {
    const start = this._toMinutes(this._startCtl()?.value)
    if (start == null) return
    const dur = this._intOrNull(this.durationTarget.value)
    if (dur != null) {
      this._setEndFromDuration(start, dur)
    } else if (this._toMinutes(this._endCtl()?.value) != null) {
      this._recalcDuration()
    }
  }

  _recalcDuration() {
    const start = this._toMinutes(this._startCtl()?.value)
    const end = this._toMinutes(this._endCtl()?.value)
    if (start == null || end == null) return
    let diff = end - start
    if (diff < 0) diff += 24 * 60
    this.durationTarget.value = diff
  }

  _setEndFromDuration(startMin, durMin) {
    const endMin = (startMin + durMin) % (24 * 60)
    this._endCtl()?.setValue(this._toHHMM(endMin))
  }

  _startCtl() {
    return this.application.getControllerForElementAndIdentifier(
      this.startTarget, "time-select"
    )
  }

  _endCtl() {
    return this.application.getControllerForElementAndIdentifier(
      this.endTarget, "time-select"
    )
  }

  _toMinutes(hhmm) {
    if (!hhmm) return null
    const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10))
    if (Number.isNaN(h) || Number.isNaN(m)) return null
    return h * 60 + m
  }

  _toHHMM(min) {
    const h = Math.floor(min / 60)
    const m = min % 60
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
  }

  _intOrNull(v) {
    if (v === "" || v == null) return null
    const n = parseInt(v, 10)
    return Number.isFinite(n) && n >= 0 ? n : null
  }
}
